import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { TextField } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

import { SidebarLayout } from '@/components/layouts/SidebarLayout';
import { Action, NetworkCategory, NetworksService, User } from '@/services/api';
import { FormSection } from '@/components/form-elements/FormSection';
import { QueryKeys } from '@/services/QueryKeys';
import { useInitialFormValues } from '@/hooks/useInitialFormValues';
import { TextFieldLoading } from '@/components/form-elements/TextFieldLoading';
import { DeleteDialog } from '@/components/form-elements/DeleteDialog';
import { useAppAbility } from '@/hooks/useAppAbility';
import { useGlobalStore } from '@/stores/useGlobalStore';
import { LastSaved } from '@/components/form-elements/LastSaved';

type FormData = { name: string };

export const NetworkPage = () => {
  const { networkId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ability = useAppAbility();
  const user = useGlobalStore((state) => state.user);

  const methods = useForm<FormData>();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const [lastSaved, setLastSaved] = useState<Dayjs>();

  const queryClient = useQueryClient();
  const { data: network } = useQuery(
    QueryKeys.networks.id(networkId as string),
    () => NetworksService.findOne({ id: networkId as string }),
    { enabled: !!networkId },
  );

  useInitialFormValues<Omit<FormData, 'networks'>>({
    entity: network,
    useFormReturn: methods,
    fields: ['name'],
  });

  const { mutate } = useMutation(
    (formData: FormData) => NetworksService.update({ id: networkId as string, requestBody: formData }),
    {
      onMutate: () => setLastSaved(undefined),
      onSuccess: () => {
        setLastSaved(dayjs());

        return queryClient.invalidateQueries(QueryKeys.networks.id(networkId as string));
      },
    },
  );
  const { mutate: mutateRemove } = useMutation(() => NetworksService.remove({ id: networkId as string }), {
    onSuccess: async () => {
      navigate(
        `/beratung/kategorien?tab=${Object.values(NetworkCategory).indexOf(network?.category as NetworkCategory)}`,
      );
      await queryClient.invalidateQueries(QueryKeys.networks.all);
    },
  });

  const isLoading = !network;
  const entityLabel = t(`NetworkCategory.${network?.category}`);

  return (
    <SidebarLayout buttonLabel="Speichern" actionArea={<LastSaved lastSaved={lastSaved} />}>
      <form
        id="main-form"
        onSubmit={handleSubmit((data) => {
          mutate(data);
        })}
      >
        <FormSection title={entityLabel}>
          <TextFieldLoading isLoading={isLoading}>
            <TextField label="Name" {...register('name', { required: true })} error={!!errors.name} />
          </TextFieldLoading>
        </FormSection>
      </form>

      <DeleteDialog
        onDelete={mutateRemove}
        entityLabel={entityLabel}
        entityName={network?.name as string}
        can={ability.can(Action.DELETE, user as User)}
      >
        Durch das Löschen werden auch etwaige Zuordnungen zu Organisationen entfernt.
      </DeleteDialog>
    </SidebarLayout>
  );
};
